import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { ref, getDatabase, get, set, update } from "firebase/database";
import { toastrError, toastrSuccess } from "../components/toastr";
import { v4 } from "uuid";

function generatePin() {
  return String(Math.floor(100000 + Math.random() * 900000));
}

async function uniquePin(db) {
  let pin;
  let attempts = 0;
  do {
    pin = generatePin();
    const snap = await get(ref(db, `pinMap/${pin}`));
    if (!snap.exists()) break;
    attempts++;
  } while (attempts < 10);
  return pin;
}

function StaffSettings() {
  const navigate = useNavigate();
  const ownerId = localStorage.getItem("ownerId");
  const db = getDatabase();

  const [waiters, setWaiters] = useState([]);
  const [newName, setNewName] = useState("");
  const [adding, setAdding] = useState(false);
  const [revealedPins, setRevealedPins] = useState({});
  const [newPinInfo, setNewPinInfo] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState("");

  const loadWaiters = useCallback(async () => {
    const snap = await get(ref(db, `waiters/${ownerId}`));
    setWaiters(snap.exists() ? Object.values(snap.val()) : []);
  }, [db, ownerId]);

  useEffect(() => {
    if (ownerId) loadWaiters();
  }, [ownerId, loadWaiters]);

  async function handleAdd() {
    if (!newName.trim()) return;
    setAdding(true);
    try {
      const pin = await uniquePin(db);
      const waiterId = v4();
      const waiterData = {
        id: waiterId,
        name: newName.trim(),
        pin,
        isActive: true,
        createdAt: Date.now(),
        lastLogin: null,
      };
      await set(ref(db, `waiters/${ownerId}/${waiterId}`), waiterData);
      await set(ref(db, `pinMap/${pin}`), {
        ownerId,
        waiterId,
        name: newName.trim(),
        isActive: true,
      });
      setNewPinInfo({ name: newName.trim(), pin });
      setNewName("");
      await loadWaiters();
    } catch (err) {
      toastrError("Could not add staff");
    }
    setAdding(false);
  }

  async function handleSaveName(waiter) {
    if (!editName.trim()) return;
    try {
      await update(ref(db, `waiters/${ownerId}/${waiter.id}`), {
        name: editName.trim(),
      });
      await update(ref(db, `pinMap/${waiter.pin}`), { name: editName.trim() });
      setEditingId(null);
      await loadWaiters();
      toastrSuccess("Name updated");
    } catch (err) {
      toastrError("Could not update name");
    }
  }

  async function handleRegenPin(waiter) {
    try {
      await update(ref(db, `pinMap/${waiter.pin}`), { isActive: false });
      const newPin = await uniquePin(db);
      await update(ref(db, `waiters/${ownerId}/${waiter.id}`), { pin: newPin });
      await set(ref(db, `pinMap/${newPin}`), {
        ownerId,
        waiterId: waiter.id,
        name: waiter.name,
        isActive: true,
      });
      setNewPinInfo({ name: waiter.name, pin: newPin });
      await loadWaiters();
    } catch (err) {
      toastrError("Could not regenerate PIN");
    }
  }

  async function handleToggleActive(waiter) {
    const next = !waiter.isActive;
    try {
      await update(ref(db, `waiters/${ownerId}/${waiter.id}`), { isActive: next });
      await update(ref(db, `pinMap/${waiter.pin}`), { isActive: next });
      await loadWaiters();
    } catch (err) {
      toastrError("Could not update staff");
    }
  }

  return (
    <div className="max-w-md mx-auto pb-10">
      {/* Header */}
      <div className="px-5 pt-6 pb-4 flex items-center gap-3">
        <button
          onClick={() => navigate("/settings")}
          className="w-9 h-9 rounded-xl bg-gray-100 flex items-center justify-center text-gray-600"
        >
          <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 19l-7-7 7-7" />
          </svg>
        </button>
        <div>
          <h1 className="text-xl font-bold text-gray-900">Staff Management</h1>
          <p className="text-xs text-gray-500">Add and manage your waiters</p>
        </div>
      </div>

      <div className="px-5 flex flex-col gap-y-4">
        {/* New PIN alert */}
        {newPinInfo && (
          <div className="bg-purple-50 border border-purple-200 rounded-xl p-4">
            <p className="text-sm font-semibold text-purple-900">
              Share this PIN with {newPinInfo.name}
            </p>
            <div className="flex gap-2 mt-2">
              {newPinInfo.pin.split("").map((d, i) => (
                <span
                  key={i}
                  className="w-9 h-10 bg-white border border-purple-300 rounded-lg flex items-center justify-center text-lg font-bold text-primary"
                >
                  {d}
                </span>
              ))}
            </div>
            <p className="text-xs text-purple-500 mt-2">
              This PIN will not be shown again. Save it now.
            </p>
            <button
              onClick={() => setNewPinInfo(null)}
              className="mt-2 text-xs text-purple-600 font-semibold underline"
            >
              Done, dismiss
            </button>
          </div>
        )}

        {/* Add staff */}
        <div className="bg-white rounded-2xl shadow-card border border-gray-100 p-5">
          <h2 className="text-sm font-bold text-gray-900 mb-3">Add Staff</h2>
          <div className="flex gap-2">
            <input
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && handleAdd()}
              className="flex-1 px-3 py-2.5 bg-gray-50 rounded-xl border border-gray-200 text-sm outline-none focus:border-primary"
              placeholder="Staff name"
            />
            <button
              onClick={handleAdd}
              disabled={!newName.trim() || adding}
              className={`px-4 py-2.5 rounded-xl text-sm font-semibold ${
                newName.trim() && !adding
                  ? "bg-primary text-white"
                  : "bg-gray-100 text-gray-400 cursor-not-allowed"
              }`}
            >
              {adding ? "…" : "Add"}
            </button>
          </div>
        </div>

        {/* Staff list */}
        <div className="bg-white rounded-2xl shadow-card border border-gray-100 p-5">
          <h2 className="text-sm font-bold text-gray-900 mb-3">
            All Staff ({waiters.length})
          </h2>
          {waiters.length === 0 && (
            <p className="text-sm text-gray-400 text-center py-4">
              No staff added yet.
            </p>
          )}
          {waiters.map((w) => (
            <div
              key={w.id}
              className={`border rounded-xl px-4 py-3 mb-2 ${
                w.isActive ? "border-gray-100" : "border-gray-100 bg-gray-50 opacity-60"
              }`}
            >
              {/* Name row */}
              {editingId === w.id ? (
                <div className="flex gap-2 mb-2">
                  <input
                    value={editName}
                    onChange={(e) => setEditName(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") handleSaveName(w);
                      if (e.key === "Escape") setEditingId(null);
                    }}
                    autoFocus
                    className="flex-1 px-3 py-1.5 bg-gray-50 rounded-lg border border-primary text-sm outline-none"
                  />
                  <button
                    onClick={() => handleSaveName(w)}
                    className="px-3 py-1.5 bg-primary text-white text-xs font-semibold rounded-lg"
                  >
                    Save
                  </button>
                  <button
                    onClick={() => setEditingId(null)}
                    className="px-3 py-1.5 bg-gray-100 text-gray-600 text-xs font-semibold rounded-lg"
                  >
                    Cancel
                  </button>
                </div>
              ) : (
                <div className="flex items-center justify-between mb-1">
                  <p className="text-sm font-semibold text-gray-800">{w.name}</p>
                  <button
                    onClick={() => { setEditingId(w.id); setEditName(w.name); }}
                    className="text-xs text-gray-400 hover:text-primary"
                  >
                    ✏️ Edit name
                  </button>
                </div>
              )}

              {/* PIN row */}
              <p className="text-xs text-gray-400">
                PIN:{" "}
                {revealedPins[w.id] ? w.pin : "••••••"}{" "}
                ·{" "}
                <button
                  onClick={() =>
                    setRevealedPins((p) => ({ ...p, [w.id]: !p[w.id] }))
                  }
                  className="text-primary underline"
                >
                  {revealedPins[w.id] ? "hide" : "show"}
                </button>
              </p>

              {/* Actions */}
              <div className="flex gap-2 mt-2">
                <button
                  onClick={() => handleRegenPin(w)}
                  disabled={!w.isActive}
                  className={`text-xs font-medium border rounded-lg px-2.5 py-1.5 ${
                    w.isActive
                      ? "text-primary border-purple-200"
                      : "text-gray-300 border-gray-200 cursor-not-allowed"
                  }`}
                >
                  New PIN
                </button>
                <button
                  onClick={() => handleToggleActive(w)}
                  className={`text-xs font-medium border rounded-lg px-2.5 py-1.5 ${
                    w.isActive
                      ? "text-red-500 border-red-200"
                      : "text-green-600 border-green-200"
                  }`}
                >
                  {w.isActive ? "Disable" : "Enable"}
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default StaffSettings;
